import React from 'react';
import { FileCopyOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import ContextSingleton from '../__singletons__/context-singleton';
import { ALERT_SEVERITY_ENUM } from '../enums';
import { useUiStyles } from '../hooks';
import { mui } from '../libs';
import { useDispatch } from '../libs';
import { Alert } from '../types/common';

/**
 * Component that renders a code block with copy-to-clipboard functionality
 *
 * @param content - The code content to display
 * @param lines - Optional JSX elements to render instead of raw content
 * @param small - Optional flag to enable compact styling
 *
 * @returns Code block component with copy button and snackbar notifications
 */

const CodeBlock = ({ content, lines, small }: { content: string; lines?: React.ReactElement[]; small?: boolean }) => {
  const actions = ContextSingleton.getInstance().actions;
  const dispatch = useDispatch();
  const uiStyles = useUiStyles();

  const showNotification = (notification: Alert) => dispatch(actions.ui.showNotification(notification));

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      showNotification({ message: 'Copied to clipboard!', severity: ALERT_SEVERITY_ENUM.SUCCESS });
    } catch {
      showNotification({ message: 'Failed to copy to clipboard.', severity: ALERT_SEVERITY_ENUM.ERROR });
    }
  };

  return (
    <mui.core.Box sx={uiStyles.codeBlock}>
      <IconButton sx={uiStyles.codeBlockCopy} onClick={copyCodeToClipboard} size="small">
        <FileCopyOutlined sx={{ fontSize: '14px' }} />
      </IconButton>

      <mui.core.Typography
        component="pre"
        sx={uiStyles.codeBlockContent}
        style={small ? { padding: '0.5rem 1rem 0.5rem 0.5rem', whiteSpace: 'normal', minHeight: '32px' } : {}}
      >
        {lines ? lines : content}
      </mui.core.Typography>
    </mui.core.Box>
  );
};

export default CodeBlock;
